import React, { useState, useEffect, useRef, useCallback } from 'react';
import Swal from 'sweetalert2';

const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [registration, setRegistration] = useState(null);
  const registrationRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const idleCheckIntervalRef = useRef(null);
  const IDLE_TIME_MS = 30000; // 30 seconds of inactivity before auto-update

  // Helper functions (memoized with useCallback to avoid dependency issues)
  const checkSafeToUpdateHelper = useCallback(async () => {
    try {
      // Check if sync is in progress
      if (window.__wealthbag_sync_in_progress) {
        return false;
      }

      // Check if token refresh is in progress
      if (window.__wealthbag_tokenRefreshing) {
        return false;
      }

      // Check if there's a bulk sync lock
      const bulkSyncLock = localStorage.getItem('wealthbag_bulk_sync_lock');
      if (bulkSyncLock) {
        const lockTime = parseInt(bulkSyncLock, 10);
        const lockAge = Date.now() - lockTime;
        // If lock is recent (less than 30 seconds), don't update
        if (lockAge < 30000) {
          return false;
        }
      }

      // Check if IndexedDB operations are pending
      // Wait a bit to ensure any pending operations complete
      await new Promise(resolve => setTimeout(resolve, 500));

      return true;
    } catch (error) {
      console.error('Error checking if safe to update:', error);
      return false;
    }
  }, []);

  const performSafeUpdateHelper = useCallback(async () => {
    const reg = registrationRef.current || registration;
    if (!reg || !reg.waiting) {
      return;
    }

    try {
      // Ensure all localStorage operations are complete
      // Force a synchronous flush by reading/writing
      localStorage.setItem('wealthbag_update_pending', 'true');
      
      // Small delay to ensure localStorage is written
      await new Promise(resolve => setTimeout(resolve, 100));

      // Send message to waiting service worker to skip waiting
      reg.waiting.postMessage({ type: 'SKIP_WAITING' });

      // Wait a bit for the service worker to activate
      await new Promise(resolve => setTimeout(resolve, 200));

      // Clear the update flag and reload
      localStorage.removeItem('wealthbag_update_pending');
      window.location.reload();
    } catch (error) {
      console.error('Error during safe update:', error);
      // If update fails, show error
      Swal.fire({
        icon: 'error',
        title: 'Update Failed',
        text: 'Failed to update. Please try again.',
        confirmButtonColor: '#2ca01c',
      });
    }
  }, [registration]);

  useEffect(() => {
    // Check if app is already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
    }

    // Listen for the beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      // Stash the event so it can be triggered later
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Check if app was installed
    window.addEventListener('appinstalled', () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
      Swal.fire({
        icon: 'success',
        title: 'App Installed!',
        text: 'Deco Master POS has been installed successfully.',
        confirmButtonColor: '#2ca01c',
      });
    });

    // Check for service worker updates
    let updateInterval = null;
    let reg = null;
    const handleUpdateFound = () => {
      const newWorker = reg.installing;
      if (newWorker) {
        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
            // New service worker is waiting
            setUpdateAvailable(true);
            // Start checking for idle state if not already started
            if (!idleCheckIntervalRef.current) {
              idleCheckIntervalRef.current = setInterval(checkIdleAndUpdate, 5000); // Check every 5 seconds
            }
          }
        });
      }
    };

    const handleVisibilityChange = () => {
      if (!document.hidden && reg) {
        reg.update();
      }
    };

    // Track user activity (keyboard, mouse, touch, scroll)
    const updateActivity = () => {
      lastActivityRef.current = Date.now();
    };

    const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    activityEvents.forEach(event => {
      document.addEventListener(event, updateActivity, { passive: true });
    });

    // Check if user is idle and safe to update
    const checkIdleAndUpdate = async () => {
      // Check directly from ref to avoid stale closure issues
      const reg = registrationRef.current;
      if (!reg || !reg.waiting) {
        return;
      }

      const timeSinceLastActivity = Date.now() - lastActivityRef.current;
      const isIdle = timeSinceLastActivity >= IDLE_TIME_MS;

      if (isIdle) {
        // Check if it's safe to update (no critical operations in progress)
        // Use the component-level function
        const isSafeToUpdate = await checkSafeToUpdateHelper();
        
        if (isSafeToUpdate) {
          console.log('User is idle and safe to update. Auto-updating...');
          await performSafeUpdateHelper();
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const checkForServiceWorkerUpdates = async () => {
      if ('serviceWorker' in navigator) {
        try {
          // Use global registration if available, otherwise get ready registration
          reg = window.serviceWorkerRegistration;
          if (!reg) {
            reg = await navigator.serviceWorker.ready;
          }
          
          setRegistration(reg);
          registrationRef.current = reg;
          
          // Check if there's a waiting service worker
          if (reg.waiting) {
            setUpdateAvailable(true);
            // Start checking for idle state if not already started
            if (!idleCheckIntervalRef.current) {
              idleCheckIntervalRef.current = setInterval(checkIdleAndUpdate, 5000); // Check every 5 seconds
            }
          }

          // Listen for updates
          reg.addEventListener('updatefound', handleUpdateFound);

          // Periodically check for updates
          const checkForUpdates = () => {
            if (reg) {
              reg.update();
            }
          };

          // Check for updates every hour
          updateInterval = setInterval(checkForUpdates, 60 * 60 * 1000);

          // Also check when the page becomes visible again
          document.addEventListener('visibilitychange', handleVisibilityChange);
        } catch (error) {
          console.error('Error checking for service worker updates:', error);
        }
      }
    };

    checkForServiceWorkerUpdates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      activityEvents.forEach(event => {
        document.removeEventListener(event, updateActivity);
      });
      if (reg) {
        reg.removeEventListener('updatefound', handleUpdateFound);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (updateInterval) {
        clearInterval(updateInterval);
      }
      if (idleCheckIntervalRef.current) {
        clearInterval(idleCheckIntervalRef.current);
      }
    };
  }, [checkSafeToUpdateHelper, performSafeUpdateHelper]);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      return;
    }

    // Show the install prompt
    deferredPrompt.prompt();

    // Wait for the user to respond to the prompt
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      console.log('User accepted the install prompt');
    } else {
      console.log('User dismissed the install prompt');
    }

    // Clear the deferredPrompt
    setDeferredPrompt(null);
  };

  const handleUpdateClick = async () => {
    if (!registration || !registration.waiting) {
      return;
    }

    // Check if safe to update before proceeding
    const isSafe = await checkSafeToUpdateHelper();
    if (!isSafe) {
      Swal.fire({
        icon: 'warning',
        title: 'Update Delayed',
        text: 'Please wait for current operations to complete before updating.',
        confirmButtonColor: '#2ca01c',
      });
      return;
    }

    await performSafeUpdateHelper();
  };


  const handleCancelUpdate = () => {
    setUpdateAvailable(false);
    // Check again after 5 minutes if update is still available
    setTimeout(() => {
      const reg = registrationRef.current;
      if (reg && reg.waiting) {
        setUpdateAvailable(true);
      }
    }, 5 * 60 * 1000); // 5 minutes
  };

  // Priority: Show install prompt first if app is not installed
  // Only show update prompt if app is already installed
  if (!isInstalled && deferredPrompt) {
    // Show install prompt
    return (
      <div
        style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          zIndex: 9999,
          background: 'linear-gradient(135deg, #2ca01c 0%, #1f6f14 100%)',
          color: 'white',
          padding: '16px 24px',
          borderRadius: '12px',
          boxShadow: '0 4px 20px rgba(44, 160, 28, 0.3)',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          maxWidth: '350px',
          cursor: 'pointer',
        }}
        onClick={handleInstallClick}
      >
        <div>
          <i className="bi bi-download" style={{ fontSize: '24px' }}></i>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
            Install Decor Masters POS
          </div>
          <div style={{ fontSize: '12px', opacity: 0.9 }}>
            Install for offline access and faster performance
          </div>
        </div>
        <button
          style={{
            background: 'white',
            color: '#2ca01c',
            border: 'none',
            borderRadius: '6px',
            padding: '8px 16px',
            fontWeight: 'bold',
            cursor: 'pointer',
          }}
          onClick={(e) => {
            e.stopPropagation();
            handleInstallClick();
          }}
        >
          Install
        </button>
      </div>
    );
  }

  // Show update prompt if update is available and app is installed
  if (isInstalled && updateAvailable && registration && registration.waiting) {
    return (
      <div
        style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          zIndex: 9999,
          background: 'linear-gradient(135deg, #ff6b35 0%, #d63031 100%)',
          color: 'white',
          padding: '16px 24px',
          borderRadius: '12px',
          boxShadow: '0 4px 20px rgba(255, 107, 53, 0.3)',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          maxWidth: '350px',
        }}
      >
        <div>
          <i className="bi bi-arrow-clockwise" style={{ fontSize: '24px' }}></i>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
            Update Available
          </div>
          <div style={{ fontSize: '12px', opacity: 0.9 }}>
            A new version is available. Update now?
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            style={{
              background: 'white',
              color: '#ff6b35',
              border: 'none',
              borderRadius: '6px',
              padding: '8px 16px',
              fontWeight: 'bold',
              cursor: 'pointer',
            }}
            onClick={handleUpdateClick}
          >
            Update
          </button>
          <button
            style={{
              background: 'transparent',
              color: 'white',
              border: '1px solid white',
              borderRadius: '6px',
              padding: '8px 16px',
              fontWeight: 'bold',
              cursor: 'pointer',
            }}
            onClick={handleCancelUpdate}
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  // Don't show anything if no prompts available
  return null;

};

export default PWAInstallPrompt;

